<template>
  <div class="dashboard drawer" :class="{ 'drawer-open': sideBar.opened }">
    <input id="drawer-toggle" type="checkbox" class="drawer-toggle" />
    <div class="drawer-content flex flex-col">
      <div v-if="loading" class="space-y-2 p-2">
        <Skeleton
          v-for="i in 8"
          :key="i"
          shape="rectangle"
          class="w-full"
          height="44px" />
      </div>

      <div class="inner" v-if="!loading">
        <div class="mb-1.5 flex content-start items-start gap-x-2">
          <div class="my-auto flex w-full items-center justify-between">
            <div class="flex items-center gap-2">
              <button
                v-if="!sideBar.opened"
                class="drawer-button w-fit-content btn relative drop-shadow-sm btn-xs"
                :class="{ 'hidden': allDocuments.length === 0 }"
                @click="sideBar.opened = true">
                <ListFilterIcon :size="16" />
                <div
                  class="absolute -top-2 -right-3 badge border-red-200 bg-red-200 badge-sm"
                  v-if="appliedFilterCount > 0">
                  {{ appliedFilterCount }}
                </div>
              </button>
              <h2>Documents</h2>

              <template v-if="!isClientRole">
                <template v-if="selectionMode !== 'none'">
                  <AppButton @click="cancelSelection">Cancel</AppButton>
                  <AppButton primary @click="saveSelection">
                    {{ selectionMode === 'aiExtraction' ? 'Start Extraction' : 'Save' }}
                  </AppButton>
                  <Spinner v-if="isSavingPublishStatus || isSavingExtraction" size="sm" class="align-middle" />
                </template>

                <template v-else>
                  <Tooltip text="Publishing will allow clients to view selected documents" position="top">
                    <AppButton @click="enterPublishMode">Publish</AppButton>
                  </Tooltip>
                  <Tooltip
                    text="Send selected documents to AI for data extraction using their mapped prompts"
                    position="top">
                    <AppButton @click="enterAIExtractionMode">AI Extraction</AppButton>
                  </Tooltip>
                </template>
              </template>
            </div>

            <div class="flex flex-col gap-3 md:flex-row md:items-center">
              <label class="input input-sm flex min-w-96 items-center gap-2">
                <input type="text" placeholder="Search" v-model="searchFilter" class="grow" />
                <XIcon v-if="searchFilter" :size="20" @click="searchFilter = ''" />
              </label>

              <Toggle id="show-current-only" v-model="showCurrentOnly" label="Show Current Only" />

              <!-- restrict document upload by permission for only client users -->

              <AppButton
                v-if="uploadEnabled && (user.hasPermissions(UserPermission.DocumentsWrite) || isBrokerRole)"
                primary
                @click="$router.push(`/account/${accountId}/documents/upload`)">
                Upload Documents
              </AppButton>
            </div>
          </div>
        </div>

        <div v-if="(documents || []).length === 0" class="mt-16 flex justify-center">
          <h2 class="text-2xl text-gray-500">No documents found</h2>
        </div>

        <div v-if="documents && documents.length > 0">
          <div class="table-hscroller">
            <table id="documents-table" v-tablesort class="dense mt-4">
              <thead>
                <tr>
                  <td class="rounded-tl-md rounded-bl-md">Year</td>
                  <td>Type</td>
                  <td v-if="!isClientRole" class="nosort text-right">
                    <template v-if="selectionMode !== 'none'">
                      <div class="flex justify-end" @click.stop>
                        <Checkbox
                          :model-value="selectAll"
                          class="!p-0 [&_.label]:!p-0"
                          @update:model-value="(v: boolean) => { selectAll = v; toggleSelectAll(); }" />
                      </div>
                    </template>
                    <template v-else> &nbsp; </template>
                  </td>
                  <td>File name</td>
                  <td v-if="isParentAccount">Account Name</td>
                  <td>Policy Number</td>
                  <td>Carrier</td>
                  <td>Coverage Type</td>
                  <td v-if="canViewAIResults">AI Status</td>
                  <td>Tags</td>
                  <td class="dataindex text-right">Uploaded</td>
                  <td class="actions rounded-tr-md rounded-br-md">&nbsp;</td>
                </tr>
              </thead>

              <tbody>
                <ContextMenuRoot
                  v-for="(document, index) in documents"
                  :key="index">
                  <ContextMenuTrigger as-child>
                    <tr @contextmenu="setSelectedDocument(document)">
                      <td>{{ document.policyEffectiveYear || '' }}</td>
                      <td>{{ document.documentTypeName }}</td>
                      <td
                        v-if="!isClientRole"
                        class="text-right">
                        <template v-if="selectionMode !== 'none' && document.documentDomainName">
                          <div class="flex justify-end">
                            <Checkbox
                              v-if="selectionMode === 'publish' && document.documentDomainName === DocumentDomainTypeClientUploaded"
                              :model-value="true"
                              disabled
                              class="!p-0 [&_.label]:!p-0" />
                            <div
                              v-else-if="selectionMode === 'aiExtraction' && document.mappedPromptId == null"
                              data-tip="No Mapped Prompt"
                              class="tooltip tooltip-top top-0.5 before:max-w-sm">
                              <Checkbox
                                :model-value="false"
                                disabled
                                class="!p-0 [&_.label]:!p-0" />
                            </div>
                            <Checkbox
                              v-else
                              v-model="document.selected"
                              class="!p-0 [&_.label]:!p-0" />
                          </div>
                        </template>

                        <template v-else>
                          <div
                            v-if="document.published"
                            data-tip="This document is published and viewable by client users"
                            class="tooltip tooltip-top top-0.5 before:max-w-sm">
                            <Users
                              class="text-gray-500"
                              :size="16" />
                          </div>
                          <div
                            v-else-if="document.documentDomainName === DocumentDomainTypeClientUploaded"
                            data-tip="This document was uploaded by a client"
                            class="tooltip tooltip-top top-0.5 before:max-w-sm">
                            <Users
                              class="text-gray-500"
                              :size="16" />
                          </div>
                        </template>
                      </td>
                      <td :title="document.displayName ?? document.fileName">
                        <a
                          @click="isDocumentTypeViewable(document) ? openDocumentModal(document) : downloadFile(document)"
                          class="hover:cursor-pointer">
                          <span v-html="filters.highlight(document.displayName ?? document.s3Name, searchText)" />
                        </a>
                      </td>
                      <td v-if="isParentAccount">{{ document.accountName }}</td>
                      <td>
                        <div v-html="filters.highlight(document.mainPolicyNumber, searchText)"></div>
                        <div
                          class="text-xs"
                          v-html="filters.highlight(document.policyDescription!, searchText)"></div>
                      </td>
                      <td>{{ document.carrierName }}</td>
                      <td>{{ document.coverageType }}</td>
                      <td v-if="canViewAIResults">
                        <DocumentAIStatusCell
                          :status="document.aiValidationStatus"
                          :result-id="document.aiLinkedPromptResultId" />
                      </td>
                      <td>
                        <div
                          class="chips m-0 flex flex-wrap gap-1"
                          v-if="document.tags && document.tags.length > 0">
                          <Badge
                            v-for="tag in document.tags"
                            :key="document.tags.indexOf(tag)"
                            variant="outline"
                            class="border-gray-300 bg-none font-normal text-primary">
                            <div v-html="filters.highlight(tag, searchText)"></div>
                          </Badge>
                        </div>
                      </td>
                      <td
                        class="dataindex no-snapshot cursor-pointer text-right text-xs"
                        :data-index="index"
                        @click="showRelativeTime = !showRelativeTime">
                        <div v-if="showRelativeTime">{{ relativeDateTime(document.createdByDate) }}</div>
                        <div v-else>
                          {{ toDateTime(document.createdByDate ?? '').toLocaleString(DateTime.DATETIME_FULL) }}
                        </div>
                        <div v-if="!isClientRole">{{ formatCreatedByUser(document.createdByUser) }}</div>
                      </td>

                      <td class="actions">
                        <MenuDropdown :items="documentMenuItems.get(document.documentId) ?? []" size="md" left>
                          <template #action>
                            <label tabindex="0" class="btn btn-square btn-ghost btn-sm" data-testid="document-row-menu">
                              <EllipsisVertical :size="20" />
                            </label>
                          </template>
                        </MenuDropdown>
                      </td>
                    </tr>
                  </ContextMenuTrigger>
                  <ContextMenuPortal to="body">
                    <ContextMenuContent
                      class="max-w-80 min-w-60 gap-1 rounded-md bg-white p-2 text-sm outline-1 outline-gray-300 drop-shadow-xl"
                      @interact-outside="outsideClicked = true">
                      <!-- View Document -->
                      <ContextMenuItem
                        class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                        @click="viewSelectedDocument">
                        View Document
                      </ContextMenuItem>

                      <!-- Download Document -->
                      <ContextMenuItem
                        class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                        @click="downloadSelectedDocument">
                        Download Document
                      </ContextMenuItem>

                      <!-- Edit Document -->
                      <ContextMenuItem
                        class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                        @click="openDocumentEditModal(selectedDocument)">
                        Edit Document
                      </ContextMenuItem>

                      <!-- Delete Document -->
                      <ContextMenuItem
                        class="flex h-6 max-w-60 items-center rounded-sm p-4 text-agency-red outline-none select-none hover:bg-gray-200"
                        @click="handleDeleteModalOpen">
                        Delete Document
                      </ContextMenuItem>

                      <!-- Process Binder (Admin only) -->
                      <ContextMenuItem
                        v-if="isAdminRole && selectedDocument?.documentTypeName === 'Binder'"
                        class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                        @click="openProcessBinderModal(selectedDocument!)">
                        Process Binder
                      </ContextMenuItem>

                      <ContextMenuSeparator
                        v-if="(selectedDocument?.accountId && isParentAccount) || (!isClientRole && document.carrierId)"
                        class="my-2 h-px bg-gray-300" />

                      <!-- View Account, if parent account -->
                      <ContextMenuItem v-if="document?.accountId && isParentAccount"
                        class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                        @click="$router.push(`/account/${selectedDocument?.accountId}/overview`)">
                        View {{ selectedDocument?.accountName }}
                      </ContextMenuItem>

                      <!-- View Carrier -->
                      <ContextMenuItem
                        v-if="document.carrierId && !isClientRole"
                        class="flex h-6 max-w-60 items-center rounded-sm p-4 outline-none select-none hover:bg-gray-200"
                        @click="$router.push(`/carrier/${document.carrierId}/overview`)">
                        View {{ selectedDocument?.carrierName }}
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenuPortal>
                </ContextMenuRoot>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div
      class="drawer-side max-w-80 overflow-hidden border-r">
      <aside
        class="flex h-screen max-w-60 min-w-60 flex-col overflow-y-auto border-r border-gray-300 bg-gray-100 p-4 pb-20">
        <nav class="sticky top-0">
          <div class="mb-4 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Filters</h2>
              <button
                v-if="hasFiltersSelected"
                class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                @click="clearFilters">
                Clear All
              </button>
            </div>
            <div class="flex gap-2">
              <Tooltip text="Close" position="top">
                <button
                  class="btn btn-square btn-ghost btn-sm"
                  @click="toggleSideBarPin">
                  <XIcon :size="16" />
                </button>
              </Tooltip>
            </div>
          </div>

          <AccordionRoot type="multiple" collapsible class="flex flex-col gap-4">
            <!-- Domain Filter (Category) -->
            <AccordionItem value="domainFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'domain')">
              <AccordionHeader>
                <AccordionTrigger class="group mb-1.5 flex w-full items-center justify-between text-left">
                  <div class="flex items-center gap-2">
                    <ChevronRightIcon class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-30 group-data-[state=open]:rotate-90" />
                    <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Category</h2>
                    <span
                      v-if="hasFilteredItems('domain')"
                      class="badge badge-soft badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'domain')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('domain')"
                      class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'domain')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="overflow-hidden">
                <div class="flex flex-col gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'domain')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                    :class="{
                      'hidden': option.disabled,
                      'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'domain')!, option)">
                    <FolderIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Document Type Filter -->
            <AccordionItem value="typeFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'document-type')">
              <AccordionHeader>
                <AccordionTrigger class="group mb-1.5 flex w-full items-center justify-between text-left">
                  <div class="flex items-center gap-2">
                    <ChevronRightIcon class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-30 group-data-[state=open]:rotate-90" />
                    <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Type</h2>
                    <span
                      v-if="hasFilteredItems('document-type')"
                      class="badge badge-soft badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'document-type')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('document-type')"
                      class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'document-type')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="overflow-hidden">
                <div class="flex flex-col gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'document-type')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                    :class="{
                      'hidden': option.disabled,
                      'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'document-type')!, option)">
                    <FileTextIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Year Filter -->
            <AccordionItem value="yearFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'year')">
              <AccordionHeader>
                <AccordionTrigger class="group mb-1.5 flex w-full items-center justify-between text-left">
                  <div class="flex items-center gap-2">
                    <ChevronRightIcon class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-30 group-data-[state=open]:rotate-90" />
                    <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Year</h2>
                    <span
                      v-if="hasFilteredItems('year')"
                      class="badge badge-soft badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'year')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('year')"
                      class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'year')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="overflow-hidden">
                <div class="flex flex-col gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'year')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                    :class="{
                      'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'year')!, option)">
                    <CalendarIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Coverage Type Filter -->
            <AccordionItem value="coverageFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'coverage-type')">
              <AccordionHeader>
                <AccordionTrigger class="group mb-1.5 flex w-full items-center justify-between text-left">
                  <div class="flex items-center gap-2">
                    <ChevronRightIcon class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-30 group-data-[state=open]:rotate-90" />
                    <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Coverage Type</h2>
                    <span
                      v-if="hasFilteredItems('coverage-type')"
                      class="badge badge-soft badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'coverage-type')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('coverage-type')"
                      class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'coverage-type')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="overflow-hidden">
                <div class="flex flex-col gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'coverage-type')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                    :class="{
                      'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'coverage-type')!, option)">
                    <ShieldIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Carrier Filter -->
            <AccordionItem value="carrierFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'carrier')">
              <AccordionHeader>
                <AccordionTrigger class="group mb-1.5 flex w-full items-center justify-between text-left">
                  <div class="flex items-center gap-2">
                    <ChevronRightIcon class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-30 group-data-[state=open]:rotate-90" />
                    <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Carrier</h2>
                    <span
                      v-if="hasFilteredItems('carrier')"
                      class="badge badge-soft badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'carrier')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('carrier')"
                      class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'carrier')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="overflow-hidden">
                <div class="flex flex-col gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'carrier')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                    :class="{
                      'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'carrier')!, option)">
                    <BuildingIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Tags Filter -->
            <AccordionItem value="tagFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'tags')">
              <AccordionHeader>
                <AccordionTrigger class="group flex w-full items-center justify-between text-left">
                  <div class="flex items-center gap-2">
                    <ChevronRightIcon class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-30 group-data-[state=open]:rotate-90" />
                    <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Tags</h2>
                    <span
                      v-if="hasFilteredItems('tags')"
                      class="badge badge-soft badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'tags')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('tags')"
                      class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'tags')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="overflow-hidden">
                <div class="flex flex-col gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'tags')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                    :class="{
                      'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'tags')!, option)">
                    <TagIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </AccordionRoot>
        </nav>
      </aside>
    </div>

    <DocumentViewerModal
      :show="showDocumentModal"
      :fileName="selectedDocument?.fileName ?? 'undefined'"
      :fetchDocument="fetchDocument"
      @update:show="showDocumentModal = $event"
      @close="closeDocumentModal" />

    <DocumentEditModal
      v-if="!isClientRole && selectedDocument"
      ref="editDocumentModal"
      title="Edit Document"
      :document="selectedDocument"
      :policy-document-types="allDocumentTypes.filter((t) => t.domainName === selectedDocument?.documentDomainName)"
      :show="showDocumentEditModal"
      :include-tags="true"
      @close="closeDocumentEditModal"
      @update="(request) => updateDocumentInfo(<DocumentWithOtherData>selectedDocument, request)" />

    <ProcessBinderModal
      v-if="isAdminRole"
      ref="processBinderModal"
      @processed="handleBinderProcessed" />

    <DocumentDeleteConfirmModal
      v-if="!isClientRole && selectedDocument"
      :show="showDeleteModal"
      :document-id="selectedDocument?.documentId ?? null"
      :account-id="account.accountID ? Number(account.accountID) : null"
      :file-name="selectedDocument?.displayName ?? selectedDocument?.fileName"
      @close="handleDeleteModalClose"
      @deleted="handleDocumentDeleted" />
  </div>
</template>

<script lang="ts">
import emptySrc from '@/assets/images/filing.svg';
import { accountService } from '@/services/api';
import { Account } from '@/types/Account';
import { documentService } from '@/services/v2/DocumentService';
import { DocumentDomainTypeClientUploaded, DocumentType, DocumentWithOtherData, UpdateDocumentRequest } from '@/types/v2/Document';
import { toDateTime, relativeDateTime } from '@/utilities/Dates';
import { DateTime } from 'luxon';
import Skeleton from '@/components/v2/Skeleton.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import Spinner from '@/components/v2/Spinner.vue';
import MenuDropdown, { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import { useToast } from '@/composables/useToast';
import Toggle from '@/components/v2/Toggle.vue';
import DocumentAIStatusCell from '@/components/v2/DocumentAIStatusCell.vue';
import DocumentEditModal from '@/components/v2/DocumentEditModal.vue';
import DocumentDeleteConfirmModal from '@/components/v2/DocumentDeleteConfirmModal.vue';
import DocumentViewerModal from '@/components/DocumentViewerModal.vue';
import ProcessBinderModal from '@/components/document/ProcessBinderModal.vue';
import {
  Users,
  XIcon,
  ChevronRightIcon,
  FolderIcon,
  FileTextIcon,
  CalendarIcon,
  ShieldIcon,
  BuildingIcon,
  TagIcon,
  Pencil,
  Download,
  EllipsisVertical,
  Files,
  Trash2,
  Building,
  Briefcase,
  ListFilterIcon,
  Bot,
} from '@lucide/vue';
import { filters } from '@/utilities/Filters';
import { useUser } from '@/composables/useUser';
import { UserPermission } from '@/types/UserPermissions';
import { UserRole } from '@/types/UserRole';
import Badge from '@/components/Badge.vue';
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  ContextMenuRoot,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuPortal,
  ContextMenuSeparator,
} from 'reka-ui';

enum validScenarios {
  Account = 'account',
  ParentAccount = 'parentAccount',
}

interface DocumentWithSelection extends DocumentWithOtherData {
  selected: boolean;
}

interface SideBarState {
  opened: boolean;
  filterGroups: FilterGroup[];
}

interface FilterGroup {
  name: string;
  displayName: string;
  type?: string;
  domain?: string;
  options: FilterOption[];
  grouped?: boolean;
}

interface FilterOption {
  name: string;
  type?: string;
  value: string;
  selected: boolean;
  disabled: boolean; // if the option doesn't exist in the document list
}

interface DocType {
  name: string;
  domain: string;
}

export default {
  name: 'DocumentList',
  props: {
    scenario: {
      type: String,
      validator(value: validScenarios) {
        return [validScenarios.Account, validScenarios.ParentAccount].includes(value);
      },
    },
  },
  setup() {
    const user = useUser();
    const toast = useToast();
    return {
      user,
      toast,
    };
  },
  data: () => ({
    loading: true,
    emptySrc,
    account: {} as Account,
    allDocuments: [] as DocumentWithSelection[],
    showRelativeTime: true,
    showDropArea: false,
    allDocumentTypes: [] as DocumentType[],
    showCurrentOnly: false,
    selectionMode: 'none' as 'none' | 'publish' | 'aiExtraction',
    selectAll: false,
    isSavingPublishStatus: false,
    isSavingExtraction: false,
    errorMessage: '',
    selectedDocTypes: [] as (string | number)[],
    sideBar: {
      opened: true,
      filterGroups: [],
    } as SideBarState,
    searchFilter: '',
    searchTimeout: null as NodeJS.Timeout | null,
    searchText: '',
    filters,
    DocumentDomainTypeClientUploaded,
    UserPermission,
    showDocumentModal: false,
    showDocumentEditModal: false,
    showDeleteModal: false,
    selectedDocument: null as DocumentWithOtherData | null,
    outsideClicked: false,
    showProcessBinderModal: false,
  }),
  components: {
    Badge,
    Users,
    DocumentAIStatusCell,
    DocumentEditModal,
    DocumentDeleteConfirmModal,
    DocumentViewerModal,
    ProcessBinderModal,
    Toggle,
    Skeleton,
    AppButton,
    Checkbox,
    Spinner,
    MenuDropdown,
    Tooltip,
    XIcon,
    ChevronRightIcon,
    FolderIcon,
    FileTextIcon,
    CalendarIcon,
    ShieldIcon,
    BuildingIcon,
    TagIcon,
    EllipsisVertical,
    AccordionContent,
    AccordionHeader,
    AccordionItem,
    AccordionRoot,
    AccordionTrigger,
    ContextMenuRoot,
    ContextMenuTrigger,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuPortal,
    ContextMenuSeparator,
    ListFilterIcon,
  },
  computed: {
    hasFiltersSelected() {
      return this.sideBar.filterGroups.some((fg) => fg.options.some((o) => o.selected));
    },
    documentCarriers() {
      return Array.from(
        this.documents.reduce((acc, d) => {
          if (d.carrierName) {
            acc.add(d.carrierName);
          }
          return acc;
        }, new Set<string>()),
      ).sort();
    },
    documentCoverageTypes() {
      return Array.from(
        this.documents.reduce((acc, d) => {
          if (d.coverageType) {
            acc.add(d.coverageType);
          }
          return acc;
        }, new Set<string>()),
      ).sort();
    },
    documentYears() {
      return Array.from(
        this.documents.reduce((acc, d) => {
          if (d.policyEffectiveYear) {
            acc.add(d.policyEffectiveYear.toString());
          }
          return acc;
        }, new Set<string>()),
      ).sort();
    },
    documentDomains() {
      return Array.from(
        this.documents.reduce((acc, d) => {
          if (d.documentDomainName) {
            acc.add(d.documentDomainName);
          }
          return acc;
        }, new Set<string>()),
      );
    },
    documentTags() {
      return Array.from(
        this.allDocuments.reduce((acc, d) => {
          if (d.tags && d.tags.length > 0) {
            d.tags.forEach((tag) => acc.add(tag));
          }
          return acc;
        }, new Set<string>()),
      ).sort();
    },
    documentTypes() {
      const domainFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'domain');
      const accountTypeOption = domainFilterGroup?.options.find((o) => o.value === 'account');
      const policyTypeOption = domainFilterGroup?.options.find((o) => o.value === 'policy');
      const clientTypeOption = domainFilterGroup?.options.find((o) => o.value === 'client');

      return Array.from(
        this.documents
          .reduce((acc, d) => {
            if (
              (d.documentTypeName &&
                ((accountTypeOption?.selected && d.documentDomainName === 'Account') ||
                  (clientTypeOption?.selected && d.documentDomainName === 'Client') ||
                  (policyTypeOption?.selected && d.documentDomainName === 'Policy'))) ||
              (!accountTypeOption?.selected && !policyTypeOption?.selected && !clientTypeOption?.selected)
            ) {
              acc.set(d.documentTypeName, {
                name: d.documentTypeName,
                domain: d.documentDomainName,
              });
            }
            return acc;
          }, new Map<string, DocType>())
          .values(),
      ).sort();
    },
    DateTime() {
      return DateTime;
    },
    isParentAccount(): boolean {
      return this.scenario === validScenarios.ParentAccount;
    },
    accountId(): string {
      return this.$route.params.id as string;
    },
    uploadEnabled(): boolean {
      return this.scenario !== validScenarios.ParentAccount;
    },
    isClientRole() {
      return this.user.role.value === UserRole.Client;
    },
    isBrokerRole() {
      return this.user.role.value === UserRole.Broker;
    },
    isAdminRole() {
      return this.user.role.value === UserRole.Admin;
    },
    canViewAIResults(): boolean {
      return this.isAdminRole || this.isBrokerRole;
    },
    allSelected() {
      const visibleDocs =
        this.selectedDocTypes.length > 0
          ? this.allDocuments.filter((d) => this.selectedDocTypes.some((t) => t === d.documentTypeId))
          : this.allDocuments;
      return visibleDocs.reduce((acc, d) => acc && d.selected, true);
    },
    filteredDocuments(): DocumentWithSelection[] {
      let docs = this.allDocuments;

      if (this.showCurrentOnly) {
        docs = docs.filter(
          (d) => !d.policyExpirationDate || (d.policyExpirationDate && new Date(d.policyExpirationDate).getTime() > new Date().getTime()),
        );
      }

      // Document Domain
      const domainFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'domain');
      if (domainFilterGroup) {
        const selectedTypes = domainFilterGroup.options.filter((o) => o.selected).map((o) => o.value.toLowerCase());
        if (selectedTypes.length > 0) {
          docs = docs.filter((d) => selectedTypes.includes(d.documentDomainName?.toLowerCase()));
        }
      }

      // Document Type
      const documentTypeFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'document-type');
      if (documentTypeFilterGroup) {
        const selectedDocumentTypes = documentTypeFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedDocumentTypes.length > 0) {
          docs = docs.filter((d) => selectedDocumentTypes.includes(d.documentTypeName));
        }
      }

      // Year
      const yearFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'year');
      if (yearFilterGroup) {
        const selectedYears = yearFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedYears.length > 0) {
          docs = docs.filter((d) => selectedYears.includes(d.policyEffectiveYear?.toString()));
        }
      }

      // Coverage Type
      const coverageTypeFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'coverage-type');
      if (coverageTypeFilterGroup) {
        const selectedCoverageTypes = coverageTypeFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedCoverageTypes.length > 0) {
          docs = docs.filter((d) => selectedCoverageTypes.includes(d.coverageType));
        }
      }

      // Carrier
      const carrierFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'carrier');
      if (carrierFilterGroup) {
        const selectedCarriers = carrierFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedCarriers.length > 0) {
          docs = docs.filter((d) => selectedCarriers.includes(d.carrierName));
        }
      }

      // Tags
      const tagsFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'tags');
      if (tagsFilterGroup) {
        const selectedTags = tagsFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedTags.length > 0) {
          docs = docs.filter((d) => d.tags && d.tags.some((tag) => selectedTags.includes(tag)));
        }
      }

      if (this.isClientRole) {
        docs = docs.filter((d) => d.published || d.documentDomainName === DocumentDomainTypeClientUploaded);
      }

      return docs;
    },
    documents() {
      return this.filteredDocuments.filter(
        (d) =>
          (d.displayName ?? d.s3Name)?.toLowerCase().includes(this.searchText.toLowerCase()) ||
          (d.policyDescription ?? '').toLowerCase().includes(this.searchText.toLowerCase()) ||
          (d.mainPolicyNumber ?? '').toLowerCase().includes(this.searchText.toLowerCase()) ||
          (d.tags && d.tags.some((tag) => tag.toLowerCase().includes(this.searchText.toLowerCase()))),
      );
    },
    appliedFilterCount() {
      return this.sideBar.filterGroups.reduce((acc, fg) => {
        return acc + fg.options.filter((o) => o.selected).length;
      }, 0);
    },
    documentMenuItems(): Map<string, MenuItem[]> {
      const map = new Map<string, MenuItem[]>();
      for (const document of this.allDocuments) {
        map.set(document.documentId, this.buildDocumentMenuItems(document));
      }
      return map;
    },
  },
  methods: {
    hasFilteredItems(name: string) {
      const filteredItems = this.sideBar.filterGroups.find((g) => g.name === name)?.options.filter((o) => o.selected) ?? [];
      return filteredItems.length > 0;
    },
    clearFilters() {
      this.sideBar.filterGroups.forEach((fg) => {
        fg.options.forEach((o) => {
          o.selected = false;
          o.disabled = false;
        });
      });
    },
    applyFilter(filterGroup: FilterGroup, option: FilterOption) {
      option.selected = !option.selected;

      // Allow only one domain type to be selected at a time and filter out document types based on the selected domain type
      if (filterGroup.name === 'type') {
        const documentTypeFilterGroup = this.sideBar.filterGroups.find((fg) => fg.name === 'document-type');
        documentTypeFilterGroup?.options.map((o) => {
          o.disabled = false;
        });

        // clear all selections for all options for the types that are not selected

        filterGroup.options.forEach((o) => {
          if (o.value !== option.value) {
            o.selected = false;
          }
        });

        documentTypeFilterGroup?.options.forEach((o) => {
          if (o.type !== option.name) {
            o.disabled = true;
            o.selected = false;
          }
        });
      }
    },
    toggleSideBarPin() {
      this.sideBar.opened = !this.sideBar.opened;
    },
    resolveFilterGroups() {
      // Account / Policy / Client domains
      this.sideBar.filterGroups.push(
        this.documentDomains.reduce(
          (acc, domain) => {
            acc.options.push({
              name: domain,
              value: domain,
              type: domain,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'domain', displayName: 'Category', options: [], grouped: true } as FilterGroup,
        ),
      );

      // Account / Policy / Client document types
      this.sideBar.filterGroups.push(
        this.documentTypes.reduce(
          (acc, type) => {
            acc.options.push({
              name: type.name,
              value: type.name,
              type: type.domain,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'document-type', displayName: 'Type', options: [] } as FilterGroup,
        ),
      );

      // Year
      this.sideBar.filterGroups.push(
        this.documentYears.reduce(
          (acc, year) => {
            acc.options.push({
              name: year,
              value: year,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'year', displayName: 'Year', options: [] } as FilterGroup,
        ),
      );

      // Coverage Type
      this.sideBar.filterGroups.push(
        this.documentCoverageTypes.reduce(
          (acc, coverageType) => {
            acc.options.push({
              name: coverageType,
              value: coverageType,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'coverage-type', displayName: 'Coverage Type', options: [] } as FilterGroup,
        ),
      );

      // Carrier
      this.sideBar.filterGroups.push(
        this.documentCarriers.reduce(
          (acc, carrier) => {
            acc.options.push({
              name: carrier,
              value: carrier,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'carrier', displayName: 'Carrier', options: [] } as FilterGroup,
        ),
      );

      // Tags
      this.updateTagsFilterGroup();
    },
    updateTagsFilterGroup() {
      // Find existing tags filter group or create new one
      const existingTagsGroup = this.sideBar.filterGroups.find((g) => g.name === 'tags');
      const currentTags = this.documentTags;
      const selectedTags = existingTagsGroup?.options.filter((o) => o.selected).map((o) => o.value) ?? [];

      // Create new tags filter group
      const tagsGroup = currentTags.reduce(
        (acc, tag) => {
          acc.options.push({
            name: tag,
            value: tag,
            disabled: false,
            selected: selectedTags.includes(tag), // Preserve selected state
          });
          return acc;
        },
        { name: 'tags', displayName: 'Tags', options: [] } as FilterGroup,
      );

      // Replace existing tags group or add new one
      if (existingTagsGroup) {
        const index = this.sideBar.filterGroups.indexOf(existingTagsGroup);
        this.sideBar.filterGroups[index] = tagsGroup;
      } else {
        this.sideBar.filterGroups.push(tagsGroup);
      }
    },
    toDateTime,
    relativeDateTime,
    async downloadFile(doc: DocumentWithOtherData) {
      if (!doc.documentId) {
        console.log('No source document key found');
        return;
      }

      if (!doc.accountId) {
        console.log('No account ID found for document: ', doc);
        return;
      }

      try {
        let url = await documentService.downloadDocumentFile(doc.accountId, doc.documentId);
        const anchorElement = document.createElement('a');
        anchorElement.href = url;
        anchorElement.download = doc.displayName ?? doc.fileName ?? 'download';
        anchorElement.setAttribute('target', '_blank');
        anchorElement.click();
        anchorElement.remove();
      } catch (err) {
        console.error(err);
      }
    },
    async fetchDocument(convertToPdf: boolean): Promise<string> {
      if (!this.selectedDocument?.documentId || !this.selectedDocument.accountId) {
        return '';
      }

      try {
        return await documentService.downloadDocumentFile(
          this.selectedDocument.accountId,
          this.selectedDocument.documentId,
          convertToPdf,
        );
      } catch (error) {
        console.error('Error loading document:', error);
      }

      return '';
    },
    async handleDocumentDeleted() {
      this.handleDeleteModalClose();
      await this.refreshData();
    },
    async refreshData() {
      this.loading = true;
      const [account, documentTypes, allDocuments] = await Promise.all([
        accountService.findById(this.$route.params.id as string),
        documentService.getDocumentTypes(),
        this.fetchDocuments(),
      ]);

      this.allDocumentTypes = documentTypes.sort((a, b) => a.name.localeCompare(b.name));

      this.account = account;

      this.allDocuments = allDocuments.map((d) => ({ ...d, selected: d.published }));

      this.loading = false;

      return;
    },
    async fetchDocuments(): Promise<DocumentWithOtherData[]> {
      const accountId = Number(this.$route.params.id as string);
      if (this.isParentAccount) {
        return documentService.getDocumentsWithOtherDataUnderParentAccount(accountId);
      } else {
        return documentService.getDocumentsWithOtherDataByAccount(accountId);
      }
    },
    async updateDocumentInfo(document: DocumentWithOtherData, request?: UpdateDocumentRequest) {
      if (request) {
        await documentService.updateDocument(document.documentId, request);
        document.documentTypeId = request.documentTypeId ?? document.documentTypeId;
        document.documentTypeName = this.allDocumentTypes.find((t) => t.id === request.documentTypeId)?.name ?? document.documentTypeName;
        document.displayName = request.displayName ?? document.displayName;
        document.tags = request.tags ?? document.tags;
        // Update filter groups to reflect any tag changes
        this.updateTagsFilterGroup();
        this.closeDocumentEditModal();
      }
    },
    isDocSelectableInCurrentMode(doc: DocumentWithSelection): boolean {
      if (this.selectionMode === 'publish') {
        return doc.documentDomainName !== DocumentDomainTypeClientUploaded;
      }
      if (this.selectionMode === 'aiExtraction') {
        return doc.mappedPromptId != null;
      }
      return false;
    },
    enterPublishMode() {
      this.selectionMode = 'publish';
      this.selectAll = this.documents
        .filter((d) => this.isDocSelectableInCurrentMode(d))
        .reduce((acc, d) => acc && d.selected, true);
    },
    enterAIExtractionMode() {
      this.selectionMode = 'aiExtraction';
      this.documents.forEach((d) => {
        d.selected = false;
      });
      this.selectAll = false;
    },
    toggleSelectAll() {
      this.documents.forEach((d) => {
        if (this.isDocSelectableInCurrentMode(d)) {
          d.selected = this.selectAll;
        }
      });
    },
    async saveSelection() {
      if (this.selectionMode === 'publish') {
        await this.savePublishSelection();
      } else if (this.selectionMode === 'aiExtraction') {
        await this.saveAIExtractionSelection();
      }
      this.selectionMode = 'none';
      this.selectAll = false;
    },
    async savePublishSelection() {
      setTimeout(() => {
        this.isSavingPublishStatus = true;
      }, 250);

      const [docsToPublish, docsToUnpublish] = [
        this.documents.filter((d) => !d.published && d.selected),
        this.documents.filter((d) => d.published && !d.selected),
      ];

      try {
        if (docsToPublish.length > 0) {
          const publishPromises = docsToPublish.map((d) => documentService.publishDocument(d.documentId));
          await Promise.all(publishPromises);

          docsToPublish.forEach((d) => {
            d.published = true;
            d.selected = true;
          });
        }

        if (docsToUnpublish.length > 0) {
          const unpublishPromises = docsToUnpublish.map((d) => documentService.unPublishDocument(d.documentId));
          await Promise.all(unpublishPromises);

          docsToUnpublish.forEach((d) => {
            d.published = false;
            d.selected = false;
          });
        }
      } catch (e) {
        console.error(e);
        this.toast.error('An error occurred while trying to save publishing status.');
      }

      setTimeout(() => {
        this.isSavingPublishStatus = false;
      }, 350);
    },
    async saveAIExtractionSelection() {
      const toExtract = this.documents.filter((d) => d.selected && d.mappedPromptId != null);
      if (toExtract.length === 0) {
        return;
      }

      setTimeout(() => {
        this.isSavingExtraction = true;
      }, 250);

      try {
        const accountId = Number(this.$route.params.id as string);
        const res = await documentService.startAIExtraction(
          accountId,
          toExtract.map((d) => d.documentId),
        );
        if (res.failedDocumentIds.length > 0) {
          this.toast.error(`Failed to start extraction for ${res.failedDocumentIds.length} document(s).`);
        }
        if (res.enqueuedCount > 0) {
          this.toast.success(
            `Started AI extraction for ${res.enqueuedCount} document(s). They will process in the background — you can keep working.`,
          );
        }
      } catch (e) {
        console.error(e);
        this.toast.error('An error occurred while starting AI extraction.');
      }

      this.documents.forEach((d) => {
        d.selected = d.published;
      });

      setTimeout(() => {
        this.isSavingExtraction = false;
      }, 350);
    },
    cancelSelection() {
      this.documents.forEach((d) => {
        d.selected = d.published;
      });
      this.selectionMode = 'none';
      this.selectAll = false;
    },
    isDocumentTypeViewable(document: DocumentWithOtherData) {
      return document.fileName.endsWith('.pdf') || document.fileName.endsWith('.docx');
    },
    openDocumentModal(document: DocumentWithOtherData) {
      this.selectedDocument = document;
      this.showDocumentModal = true;
    },
    closeDocumentModal() {
      this.showDocumentModal = false;
      this.selectedDocument = null;
    },
    openDocumentEditModal(document?: DocumentWithOtherData | null) {
      if (!document) return;
      this.selectedDocument = document;
      this.showDocumentEditModal = true;
    },
    closeDocumentEditModal() {
      this.showDocumentEditModal = false;
      this.selectedDocument = null;
    },
    setSelectedDocument(document: DocumentWithOtherData) {
      this.selectedDocument = document;
    },
    viewSelectedDocument() {
      this.showDocumentModal = true;
    },
    downloadSelectedDocument() {
      if (!this.selectedDocument) return;
      this.downloadFile(this.selectedDocument);
    },
    handleDeleteModalOpen() {
      this.showDeleteModal = true;
    },
    handleDeleteModalClose() {
      this.showDeleteModal = false;
    },
    openDeleteModal(doc: DocumentWithOtherData) {
      this.selectedDocument = doc;
      this.showDeleteModal = true;
    },
    buildDocumentMenuItems(document: DocumentWithSelection): MenuItem[] {
      const items: MenuItem[] = [];
      if (this.isDocumentTypeViewable(document)) {
        items.push({
          label: 'View Document',
          handler: () => this.openDocumentModal(document),
          icon: Files,
        });
      }
      items.push({
        label: 'Download Document',
        handler: () => this.downloadFile(document),
        icon: Download,
      });
      if (!this.isClientRole) {
        items.push({
          label: 'Edit Document',
          handler: () => this.openDocumentEditModal(document),
          icon: Pencil,
        });
        items.push({
          label: 'Delete Document',
          handler: () => this.openDeleteModal(document),
          icon: Trash2,
          iconColorClass: 'text-error',
        });
      }
      if (document.accountId && this.isParentAccount) {
        items.push({
          label: `View ${document.accountName}`,
          handler: () => this.$router.push(`/account/${document.accountId}/overview`),
          icon: Building,
        });
      }
      if (document.carrierId && !this.isClientRole) {
        items.push({
          label: `View ${document.carrierName}`,
          handler: () => this.$router.push(`/carrier/${document.carrierId}/overview`),
          icon: Briefcase,
        });
      }
      if (this.isAdminRole && document.documentTypeName === 'Binder') {
        items.push({
          label: 'Process Binder',
          handler: () => this.openProcessBinderModal(document),
          icon: Bot,
        });
      }
      return items;
    },
    openProcessBinderModal(doc: DocumentWithOtherData) {
      this.selectedDocument = doc;
      (this.$refs.processBinderModal as InstanceType<typeof ProcessBinderModal>).show(doc);
    },
    handleBinderProcessed() {
      this.toast.success('Binder processing has started.');
    },
    formatCreatedByUser(createdByUser: string | null | undefined): string {
      if (!createdByUser) return '';
      if (createdByUser.toLowerCase().startsWith('platform@')) {
        return 'Epic';
      }
      return createdByUser;
    },
  },
  watch: {
    allSelected(newVal) {
      // if all docs are selected/published and the user deselects one, deselect the "select all" checkbox
      this.selectAll = newVal;
    },
    searchFilter(newVal) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.searchText = newVal;
      }, 400);
    },
  },
  async created(): Promise<void> {
    await this.refreshData();
    this.resolveFilterGroups();
  },
};
</script>

<style scoped>
td {
  white-space: nowrap;
}

.truncate {
  display: block;
  width: 10rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashboard.drawer {
  transform: translateZ(0);
}
</style>
